/**
 * Authentication hook
 * Supports multiple auth providers
 */
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/auth.service';
import { useAuthStore } from '@store/auth.store';
import { useNotification } from '@hooks/useNotification';
import { LoginRequest, RegisterRequest } from '../types/auth.types';
import { getAuthConfig, AuthProvider } from '@core/config/auth.config';
import { ROUTES } from '@core/constants/routes.constants';

export const useAuth = () => {
    const navigate = useNavigate();
    const { setAuth, clearAuth } = useAuthStore();
    const { showSuccess, showError } = useNotification();

    // Local login
    const loginMutation = useMutation({
        mutationFn: (credentials: LoginRequest) => authService.login(credentials),
        onSuccess: (response) => {
            setAuth(response.user, response.accessToken, response.refreshToken);
            showSuccess('Login successful!');
            navigate(ROUTES.HOME);
        },
        onError: (error: any) => {
            showError(error.message || 'Login failed');
        },
    });

    // Azure AD login - triggers redirect (doesn't return normally)
    const azureLoginMutation = useMutation({
        mutationFn: () => authService.login({} as LoginRequest), // Triggers Azure AD redirect
        onSuccess: (response) => {
            setAuth(response.user, response.accessToken, response.refreshToken);
            showSuccess('Signed in with WHO successfully!');
            navigate(ROUTES.HOME);
        },
        onError: (error: any) => {
            // Ignore redirect errors - they're expected
            if (error.message && error.message.includes('Redirecting to Azure AD')) {
                return; // This is expected - page will redirect
            }
            showError(error.message || 'Azure AD login failed');
        },
    });

    // Complete Azure AD login after redirect
    const completeAzureLoginMutation = useMutation({
        mutationFn: () => authService.completeAzureADSSOAfterRedirect(),
        onSuccess: (response) => {
            if (response) {
                setAuth(response.user, response.accessToken, response.refreshToken);
                showSuccess('Signed in with WHO successfully!');
                navigate(ROUTES.HOME);
            }
        },
        onError: (error: any) => {
            showError(error.message || 'Azure AD login failed');
        },
    });

    // Register (local only)
    const registerMutation = useMutation({
        mutationFn: (data: RegisterRequest) => authService.register(data),
        onSuccess: (response) => {
            setAuth(response.user, response.accessToken, response.refreshToken);
            showSuccess('Registration successful!');
            navigate(ROUTES.HOME);
        },
        onError: (error: any) => {
            showError(error.message || 'Registration failed');
        },
    });

    // Logout - uses redirect for Azure AD (no popup)
    const logoutMutation = useMutation({
        mutationFn: () => authService.logout(),
        onSuccess: () => {
            // For Azure AD, logout redirects the page, so this won't execute
            // But we handle it for other auth providers
            clearAuth();
            showSuccess('Logged out successfully');
            navigate(ROUTES.LOGIN);
        },
        onError: (error: any) => {
            // Ignore redirect errors - they're expected when using redirect flow
            if (error.message && error.message.includes('redirect')) {
                // This is expected - the page is redirecting for logout
                clearAuth();
                return;
            }
            showError(error.message || 'Logout failed');
        },
    });

    return {
        login: loginMutation.mutateAsync,
        loginWithAzureAD: azureLoginMutation.mutateAsync,
        completeAzureADLogin: completeAzureLoginMutation.mutateAsync,
        register: registerMutation.mutateAsync,
        logout: logoutMutation.mutateAsync,
        isLoading:
            loginMutation.isPending ||
            azureLoginMutation.isPending ||
            completeAzureLoginMutation.isPending ||
            registerMutation.isPending ||
            logoutMutation.isPending,
        isAzureAD: getAuthConfig().provider === AuthProvider.AZURE_AD,
    };
};
